import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CImage,
  CRow,
  CSpinner,
  CBadge,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserTag, faUserPlus, faClockRotateLeft, faCopy, faFlag } from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { referralRecord } from "@/constants/translation/referralRecord.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { masters } from "@/libs/routes";
import { useReferralRecord, useReferralRecordRewardHistory, useMarkReferralFraudulent } from "@/hooks/useReferralRecord";
import { setFormatDate, dateTimeFormat } from "@/utils/dateFormate";
import { TNTable } from "@/components";
import { showMarkFraudulentDialog } from "@/components/MarkFraudulentDialog";
import avatar from "@/assets/images/avatars/DefaultImg.png";
import constants from "@/libs/constant";

// API responses pass every value through the app's shared encryption round-trip, which
// stringifies booleans (true -> "true", false -> "false") — same convention as isTrue in
// src/pages/user/freelancer/[freelancer_user_id]/view/index.js.
const isTrue = (value) => value === true || value === constants.status.apiStatus.true;

const TABS = {
  REFERRER_INFO: "referrer_info",
  INVITED_USER_INFO: "invited_user_info",
  REWARD_HISTORY: "reward_history",
};

const getActiveStatusBadgeClass = (status) => {
  switch (status) {
    case "active":
      return "success";
    default:
      return "secondary";
  }
};

const getRewardStatusBadgeClass = (status) => {
  switch (status) {
    case "rewarded":
      return "success";
    default:
      return "warning";
  }
};

// Label-above-value field, matching the boxy/tabular UI reference: a small muted caption with
// the value directly beneath it, no borders/rules between fields (the containing CCard already
// provides the boundary). No text-uppercase - the reference keeps labels in plain sentence
// case, not shouty all-caps. `text-muted` (not `text-medium-emphasis`) is deliberate - this
// app's forced-dark theme (src/scss/style.scss) only overrides --cui-secondary-color/--cui-
// tertiary-color (what `text-muted` reads), not --cui-medium-emphasis, so `text-medium-emphasis`
// silently fell back to Bootstrap's light-theme default and rendered near-invisible here.
const InfoItem = ({ label, children }) => (
  <div>
    <div className="text-muted small mb-1">{label}</div>
    <div>{children}</div>
  </div>
);

// Rounded tile used for the leaderboard rank row. Same reasoning as InfoItem above: this app's
// theme override sets --cui-secondary-bg/--cui-border-color explicitly (already relied on
// everywhere else, e.g. CustomDropdown, form inputs), so referencing those variables directly
// is what actually renders correctly here - `bg-body-tertiary` reads --cui-tertiary-bg, which
// isn't one of the overridden variables and fell back to Bootstrap's near-white default.
const StatTile = ({ label, value }) => (
  <div
    className="text-center p-3 rounded-3 border h-100"
    style={{ backgroundColor: "var(--cui-secondary-bg)" }}
  >
    <div className="text-muted small mb-1">{label}</div>
    <div className="fs-3 fw-bold">{value}</div>
  </div>
);

const ViewReferralRecord = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState(TABS.REFERRER_INFO);
  const [rewardHistoryPage, setRewardHistoryPage] = useState(constants.pagination.defaultPage);

  const {
    data: response,
    isLoading,
    error: referralRecordError,
    refetch,
  } = useReferralRecord(id, null, (error) => {
    console.error("Error fetching referral record details:", error);
  });

  const referralRecordData = response?.data;

  const { data: rewardHistoryResponse, isLoading: isRewardHistoryLoading } = useReferralRecordRewardHistory(
    id,
    {
      page: rewardHistoryPage,
      limit: constants.pagination.defaultPageSize,
    },
    { enabled: activeTab === TABS.REWARD_HISTORY },
  );

  const { mutate: doMarkFraudulent } = useMarkReferralFraudulent(() => {
    toast.success(t(referralRecord.view.messages.mark_fraudulent_success));
    refetch();
  });

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <CSpinner color="primary" />
      </div>
    );
  }

  if (referralRecordError || !referralRecordData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <h4>{t(referralRecord.view.error.not_found)}</h4>
          <p>{t(referralRecord.view.error.not_found_description)}</p>
          <CButton color="primary" onClick={() => navigate(masters.referralRecords.list)}>
            {t(referralRecord.view.back_to_list)}
          </CButton>
        </div>
      </div>
    );
  }

  const na = t(referralRecord.view.defaults.na);
  const referrer = referralRecordData.referrer || {};
  const referrerInfo = referralRecordData.referrer_info || {};
  const invitedUserInfo = referralRecordData.invited_user_info || {};
  const leaderboardRank = referrerInfo.current_leaderboard_rank || {};
  const isFraudFlagged = isTrue(referralRecordData.is_fraud_flagged);

  const rewardHistoryList = rewardHistoryResponse?.data?.list || [];
  const rewardHistoryPagination = rewardHistoryResponse?.data?.pagination;

  const handleCopyReferralLink = () => {
    if (!referrerInfo.referral_link) {
      return;
    }
    navigator.clipboard
      .writeText(referrerInfo.referral_link)
      .then(() => toast.success(t(referralRecord.view.referrer_info.copy_success)));
  };

  const handleCopyReferralCode = () => {
    if (!referrerInfo.referral_code) {
      return;
    }
    navigator.clipboard
      .writeText(referrerInfo.referral_code)
      .then(() => toast.success(t(referralRecord.view.referrer_info.copy_code_success)));
  };

  const handleMarkFraudulent = () => {
    showMarkFraudulentDialog({
      t,
      referralUuid: referralRecordData.referral_uuid,
      onConfirm: (uuid, reason) => doMarkFraudulent({ uuid, reason }),
    });
  };

  const rewardHistoryColumns = [
    {
      id: "label",
      accessorKey: "label",
      header: t(referralRecord.view.reward_history.table.label_col_heading),
      cell: ({ getValue }) => <span>{getValue() || na}</span>,
    },
    {
      id: "reason",
      accessorKey: "reason",
      header: t(referralRecord.view.reward_history.table.reason_col_heading),
      cell: ({ getValue }) => <span>{getValue() || na}</span>,
    },
    {
      id: "monthsGranted",
      accessorKey: "monthsGranted",
      header: t(referralRecord.view.reward_history.table.months_granted_col_heading),
      cell: ({ getValue }) => <span>{getValue() != null ? getValue() : na}</span>,
    },
    {
      id: "earnedAt",
      accessorKey: "earnedAt",
      header: t(referralRecord.view.reward_history.table.earned_at_col_heading),
      cell: ({ getValue }) => <span>{getValue() ? dateTimeFormat(getValue()) : na}</span>,
    },
  ];

  return (
    <div className="view-referral-record-container">
      <CCard className="mb-4 profile-header-card">
        <CCardBody>
          <CRow className="align-items-center">
            <CCol md="auto" className="text-center mb-3 mb-md-0">
              <CImage
                src={referrer.avatar || avatar}
                alt="profile_img"
                className="rounded-circle"
                style={{ width: "120px", height: "120px", objectFit: "cover", border: "4px solid #212631" }}
              />
            </CCol>
            <CCol>
              <h2 className="mb-1">{referrer.name || na}</h2>
              <div className="text-muted mb-1">{referrer.email || na}</div>
              <div className="text-muted mb-2">{referrer.phone || na}</div>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <CBadge color={getActiveStatusBadgeClass(referralRecordData.active_status)}>
                  {t(referralRecord.list.active_status_filter[referralRecordData.active_status]) ||
                    referralRecordData.active_status}
                </CBadge>
                <CBadge color={getRewardStatusBadgeClass(referralRecordData.reward_status)}>
                  {t(referralRecord.list.reward_status[referralRecordData.reward_status]) ||
                    referralRecordData.reward_status}
                </CBadge>
              </div>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="referral-record-view-flag-tooltip">
                    {isFraudFlagged
                      ? t(referralRecord.list.already_flagged_tooltip)
                      : t(referralRecord.view.actions.mark_fraudulent)}
                  </Tooltip>
                }
              >
                <span>
                  <CButton
                    size="sm"
                    color="danger"
                    disabled={isFraudFlagged}
                    onClick={handleMarkFraudulent}
                    style={{ opacity: isFraudFlagged ? 0.5 : 1 }}
                  >
                    <FontAwesomeIcon icon={faFlag} className="me-1" />
                    {t(referralRecord.view.actions.mark_fraudulent)}
                  </CButton>
                </span>
              </OverlayTrigger>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CNav variant="tabs" className="mb-4 view-user-tabs">
        {[
          [TABS.REFERRER_INFO, faUserTag, referralRecord.view.tabs.referrer_info],
          [TABS.INVITED_USER_INFO, faUserPlus, referralRecord.view.tabs.invited_user_info],
          [TABS.REWARD_HISTORY, faClockRotateLeft, referralRecord.view.tabs.reward_history],
        ].map(([tabKey, icon, labelKey]) => (
          <CNavItem key={tabKey}>
            <CNavLink
              href="#"
              active={activeTab === tabKey}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab(tabKey);
              }}
            >
              <FontAwesomeIcon icon={icon} className="me-2" />
              {t(labelKey)}
            </CNavLink>
          </CNavItem>
        ))}
      </CNav>

      <CTabContent>
        <CTabPane visible={activeTab === TABS.REFERRER_INFO}>
          <CCard>
            <CCardBody>
              {/* No repeated "Referrer Info" heading here - the active tab label right above
                  already says that (matches the UI reference, which goes straight from the tab
                  bar into the fields). Every column below is md={4}, same 12-column total/
                  breakpoint as the stats row, so Referral Code lines up directly above Total
                  Invited and Referral Link above Total Active Referrals, instead of the two
                  rows using different column ratios/breakpoints and drifting out of alignment
                  at in-between viewport widths (QA finding) - the reference itself leaves the
                  3rd slot of this row empty rather than stretching Link to fill it. */}
              <CRow className="g-4 mb-4">
                <CCol md={4}>
                  <InfoItem label={t(referralRecord.view.referrer_info.referral_code_label)}>
                    {referrerInfo.referral_code ? (
                      <div className="d-flex align-items-center gap-2">
                        <span>{referrerInfo.referral_code}</span>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="referral-code-copy-tooltip">
                              {t(referralRecord.view.referrer_info.copy_code_btn)}
                            </Tooltip>
                          }
                        >
                          <CButton size="sm" color="secondary" variant="ghost" onClick={handleCopyReferralCode}>
                            <FontAwesomeIcon icon={faCopy} />
                          </CButton>
                        </OverlayTrigger>
                      </div>
                    ) : (
                      na
                    )}
                  </InfoItem>
                </CCol>
                <CCol md={4}>
                  <InfoItem label={t(referralRecord.view.referrer_info.referral_link_label)}>
                    {referrerInfo.referral_link ? (
                      <div className="d-flex align-items-center gap-2">
                        <a
                          href={referrerInfo.referral_link}
                          target="_blank"
                          rel="noreferrer"
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: "180px" }}
                        >
                          {referrerInfo.referral_link}
                        </a>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="referral-link-copy-tooltip">
                              {t(referralRecord.view.referrer_info.copy_link_btn)}
                            </Tooltip>
                          }
                        >
                          <CButton size="sm" color="secondary" variant="ghost" onClick={handleCopyReferralLink}>
                            <FontAwesomeIcon icon={faCopy} />
                          </CButton>
                        </OverlayTrigger>
                      </div>
                    ) : (
                      na
                    )}
                  </InfoItem>
                </CCol>
              </CRow>

              <CRow className="g-4 mb-4">
                <CCol md={4}>
                  <InfoItem label={t(referralRecord.view.referrer_info.total_invited_label)}>
                    {referrerInfo.total_invited != null ? referrerInfo.total_invited : na}
                  </InfoItem>
                </CCol>
                <CCol md={4}>
                  <InfoItem label={t(referralRecord.view.referrer_info.total_active_referrals_label)}>
                    {referrerInfo.total_active_referrals != null ? referrerInfo.total_active_referrals : na}
                  </InfoItem>
                </CCol>
                <CCol md={4}>
                  <InfoItem label={t(referralRecord.view.referrer_info.premium_months_earned_label)}>
                    {referrerInfo.premium_months_earned != null ? referrerInfo.premium_months_earned : na}
                  </InfoItem>
                </CCol>
              </CRow>

              <hr className="my-4" />

              <h6 className="fw-semibold mb-3">
                {t(referralRecord.view.referrer_info.leaderboard_rank_title)}
              </h6>
              <CRow className="g-3">
                <CCol md={4}>
                  <StatTile
                    label={t(referralRecord.view.referrer_info.leaderboard_city_label)}
                    value={leaderboardRank.city != null ? leaderboardRank.city : na}
                  />
                </CCol>
                <CCol md={4}>
                  <StatTile
                    label={`${t(referralRecord.view.referrer_info.leaderboard_country_label)}${
                      leaderboardRank.countryLabel ? ` (${leaderboardRank.countryLabel})` : ""
                    }`}
                    value={leaderboardRank.country != null ? leaderboardRank.country : na}
                  />
                </CCol>
                <CCol md={4}>
                  <StatTile
                    label={t(referralRecord.view.referrer_info.leaderboard_global_label)}
                    value={leaderboardRank.global != null ? leaderboardRank.global : na}
                  />
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CTabPane>

        <CTabPane visible={activeTab === TABS.INVITED_USER_INFO}>
          <CCard>
            <CCardBody>
              {/* No repeated "Invited User Info" heading - matches the Referrer Info tab above
                  (tab label already says it), following the UI reference's tighter layout. */}
              <CRow className="g-4">
                <CCol md={6}>
                  <InfoItem label={t(referralRecord.view.invited_user_info.name_label)}>
                    {invitedUserInfo.name || na}
                  </InfoItem>
                </CCol>
                <CCol md={6}>
                  <InfoItem label={t(referralRecord.view.invited_user_info.email_label)}>
                    {invitedUserInfo.email || na}
                  </InfoItem>
                </CCol>
                <CCol md={6}>
                  <InfoItem label={t(referralRecord.view.invited_user_info.registration_date_label)}>
                    {invitedUserInfo.registration_date ? setFormatDate(invitedUserInfo.registration_date) : na}
                  </InfoItem>
                </CCol>
                <CCol md={6}>
                  <InfoItem label={t(referralRecord.view.invited_user_info.active_status_label)}>
                    <CBadge color={getActiveStatusBadgeClass(invitedUserInfo.active_status)}>
                      {t(referralRecord.list.active_status_filter[invitedUserInfo.active_status]) ||
                        invitedUserInfo.active_status}
                    </CBadge>
                  </InfoItem>
                </CCol>
                <CCol md={6}>
                  {/* BUG FIX (QA finding): this used to be a conditional block that omitted the
                      whole label+value pair for a not-yet-active invitee (via `&&`) instead of
                      falling back to `na`, unlike every other field on this tab - so the field
                      simply vanished rather than showing a graceful placeholder. */}
                  <InfoItem label={t(referralRecord.view.invited_user_info.active_at_label)}>
                    {invitedUserInfo.active_at ? setFormatDate(invitedUserInfo.active_at) : na}
                  </InfoItem>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CTabPane>

        <CTabPane visible={activeTab === TABS.REWARD_HISTORY}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(referralRecord.view.reward_history.title)}</h4>
              <TNTable
                columns={rewardHistoryColumns}
                data={rewardHistoryList}
                paginationData={rewardHistoryPagination}
                onSelectPage={(pageIndex) => {
                  setRewardHistoryPage(pageIndex + 1);
                }}
                pageIndexGet={rewardHistoryPage - 1}
                isLoading={isRewardHistoryLoading}
                t={t}
              />
            </CCardBody>
          </CCard>
        </CTabPane>
      </CTabContent>
    </div>
  );
};

ViewReferralRecord.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.referralRecord?.list,
    pathname: masters.referralRecords.list,
    active: false,
  },
  {
    name: breadcrumb_key.masters?.referralRecord?.view,
    pathname: "",
    active: true,
  },
];

export default ViewReferralRecord;
